import { AppDocumentHeadOverrides } from '@ms/yammer-app-document-meta';
import { useComplianceChecks } from '@ms/yammer-compliance';
import { DeferredGlobalCopilotDrawer } from '@ms/yammer-copilot';
import { useDelegationStore } from '@ms/yammer-features-insights';
import { useCanCurrentUserAccessAdminSettings } from '@ms/yammer-hooks/dist/adminSettings';
import { useLoadFeatureAccessInsights } from '@ms/yammer-hooks/dist/featureAccessInsights';
import {
  useHasMultiTenantNetworks,
  useLoadMultiTenantOrganization,
} from '@ms/yammer-hooks/dist/multiTenantOrganization';
import { usePushSubscription } from '@ms/yammer-hooks/dist/pushSubscriptions';
import { useServiceWorkerMessages } from '@ms/yammer-hooks/dist/pwa';
import { useLoadStorylineSettings } from '@ms/yammer-hooks/dist/storylineSettings';
import { useLoadUserInformationBarrierSettings } from '@ms/yammer-hooks/dist/userInformationBarrierSettings';
import { useCurrentLocale, usePersistLocale } from '@ms/yammer-i18n-react';
import type { AppContextOptions } from '@ms/yammer-libs-app-context';
import { AppContextProvider } from '@ms/yammer-libs-app-context';
import { MediaLightboxStoreProvider } from '@ms/yammer-medialightbox';
import { useGetActiveNetworkBrandTheme } from '@ms/yammer-network-brand-theme/dist/hooks';
import { LoadNetworkBrandTheme } from '@ms/yammer-network-brand-theme/dist/integration';
import { ReduxAnnouncerBridge } from '@ms/yammer-web-components/dist/accessibility/Announcer';
import ArticleScrollProvider from '@ms/yammer-web-components/dist/articles/ArticleScrollContext';
import MediaLightbox from '@ms/yammer-web-components/dist/messageAttachments/MediaLightbox';
import { LoadableRealtimeClient } from '@ms/yammer-web-components/dist/realtime/RealtimeClient';
import SessionContextProviderWithHooks from '@ms/yammer-web-components/dist/session/SessionContextProviderWithHooks';
import { SendPresenceState } from '@ms/yammer-web-components/dist/userPresence';
import AppContainer from '@ms/yammer-web-core-ui/dist/components/AppContainer';
import KeyboardHeadingNavigation from '@ms/yammer-web-core-ui/dist/components/KeyboardHeadingNavigation';
import { LoadableToaster } from '@ms/yammer-web-core-ui/dist/fluent9/components/Toast';
import { usePrepareIcons } from '@ms/yammer-web-core-ui/dist/util/icons';
import { ContainerWidthProvider } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { useMainFeatureSettings } from '@ms/yammer-web-ui-features/dist/featureSettings';
import { UIFeaturesProvider } from '@ms/yammer-web-ui-features/dist/features';
import type { FC } from 'react';
import React, { useMemo } from 'react';

import AppLayout from '../AppLayout';
import Dialogs from '../Dialogs';
import Routes from '../Routes';
import Skiplink from '../Skiplink';
import SuiteHeaderStatusProvider from '../SuiteHeader/SuiteHeaderStatusContext';

import DeferredAppFeatures from './DeferredAppFeatures';
import { useThemeWithSuiteHeaderOverridesState } from './hooks';

const App: FC = () => {
  'use memo';

  const complianceChecks = useComplianceChecks();
  const isFeedbackEnabled = complianceChecks.policyAllowFeedback !== 'Disabled';
  const { selectedOnBehalfOfUserId } = useDelegationStore();
  const isCurrentNetworkInMto = useHasMultiTenantNetworks();

  usePersistLocale();
  useServiceWorkerMessages();
  usePushSubscription();
  usePrepareIcons();
  useLoadStorylineSettings();
  useLoadMultiTenantOrganization();
  useLoadFeatureAccessInsights({ selectedOnBehalfOfUserId });
  useLoadUserInformationBarrierSettings();

  const features = useMainFeatureSettings({
    overrides: {
      navigation: {
        admin: useCanCurrentUserAccessAdminSettings(),
      },
    },
  });

  const locale = useCurrentLocale();
  const { isUserTheme, theme } = useThemeWithSuiteHeaderOverridesState();
  const { currentActiveTheme, currentActiveThemeCoreUITokenOverrides, currentActiveThemeV9 } =
    useGetActiveNetworkBrandTheme(theme, isUserTheme);

  const appContextOptions: Partial<AppContextOptions> = useMemo(
    () => ({ isCurrentNetworkInMto }),
    [isCurrentNetworkInMto]
  );

  return (
    <AppContainer
      theme={currentActiveTheme}
      coreuiTokenOverrides={currentActiveThemeCoreUITokenOverrides}
      fluent9Theme={currentActiveThemeV9}
      locale={locale}
    >
      <KeyboardHeadingNavigation />
      <UIFeaturesProvider features={features}>
        <SessionContextProviderWithHooks>
          <AppContextProvider mainColumnAriaRole="main" options={appContextOptions}>
            <ArticleScrollProvider>
              <MediaLightboxStoreProvider>
                <AppDocumentHeadOverrides />
                <Skiplink />
                <SuiteHeaderStatusProvider>
                  <ContainerWidthProvider>
                    <AppLayout>
                      <Routes />
                    </AppLayout>
                  </ContainerWidthProvider>
                </SuiteHeaderStatusProvider>
                <Dialogs />
                <MediaLightbox />
                <LoadableRealtimeClient />
                <ReduxAnnouncerBridge />
                <SendPresenceState />
                <DeferredGlobalCopilotDrawer isFeedbackEnabled={isFeedbackEnabled} />
                <LoadableToaster />
                <DeferredAppFeatures />
              </MediaLightboxStoreProvider>
            </ArticleScrollProvider>
          </AppContextProvider>
        </SessionContextProviderWithHooks>
      </UIFeaturesProvider>
      <LoadNetworkBrandTheme shouldLoadDefaultTheme={true} />
    </AppContainer>
  );
};
export default App;
